import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';

import { Table } from 'ui-library/components/Table/Table';
import { Table as RawTable } from 'antd';
import { basicSort } from 'ui-library/components/Table/TableSorter';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import { showSuccess, showFailure } from 'actions/notificationActions';
import { getIncentiveTemplateHistory } from 'components/landlord_app/payments/store/actions';
import { IncentivesContext } from 'components/landlord_app/payments/LandlordIncentives';
import { toFriendlyDateTimeString } from 'helpers/Formatter';
import { getCurrentLocale, getTranslatedString } from 'helpers/i18n';

const StyledSectionContainer = styled.div`
  margin-bottom: 32px;
`;

const StyledTitle = styled.span`
  font-size: 18px;
  font-family: 'Circular-Bold';
  margin-bottom: 15px;
`;

const ExpandedRow = ({ record }) => {
  const [dataSource, setDataSource] = useState(null);
  const getDataSource = () => {
    return Object.keys(record.audited_changes).map((k) => {
      const change = record.audited_changes[k];
      return {
        field: k,
        changed_from:
          k === 'unit_ids'
            ? getTranslatedString('landlordPortal.incentives.unitAssociations')
            : change[0],
        changed_to:
          k === 'unit_ids'
            ? getTranslatedString('landlordPortal.incentives.revisedUnitAssociations')
            : change[1],
      };
    });
  };

  if (!dataSource) {
    setDataSource(getDataSource());
  }

  return (
    <>
      {!!dataSource && (
        <RawTable
          size="small"
          pagination={false}
          columns={[
            {
              dataIndex: 'field',
              dataType: 'text',
              key: 'field',
              ellipsis: true,
              title: getTranslatedString('landlordPortal.incentives.field'),
              width: '25%',
            },
            {
              dataIndex: 'changed_from',
              dataType: 'text',
              key: 'changed_from',
              ellipsis: true,
              title: getTranslatedString('landlordPortal.incentives.changedFrom'),
            },
            {
              dataIndex: 'changed_to',
              dataType: 'text',
              key: 'changed_to',
              ellipsis: true,
              title: getTranslatedString('landlordPortal.incentives.changedTo'),
            },
          ]}
          dataSource={dataSource}
        />
      )}
    </>
  );
};

const History = ({ showFailure, showSuccess }) => {
  const [loading, setLoading] = useState(false);
  const incentivesData = useContext(IncentivesContext);
  const currentIncentiveTemplateId = incentivesData?.currentIncentiveTemplate?.id;

  const [dataSource, setDataSource] = useState([]);

  const [filteredInfo, setFilteredInfo] = useState(null);
  const [sortedInfo, setSortedInfo] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [currentIncentiveTemplateId]);

  const loadHistory = () => {
    setLoading(false);
    getIncentiveTemplateHistory(currentIncentiveTemplateId)
      .done((data) => {
        setLoading(false);
        const ds = data.history.map((record) => {
          let name = '';
          if (
            record.auditable_type === 'Incentive' &&
            (record.action === 'destroy' || record.action === 'create')
          ) {
            name = record.audited_changes.name;
          } else if (record.auditable_type === 'Incentive' && record.action !== 'destroy') {
            const incentive = incentivesData?.currentIncentiveTemplate?.incentives.find(
              (inc) => inc.id === record.auditable_id
            );
            name = incentive ? incentive.name : '';
          }
          return {
            ...record,
            variant: name,
            created_at: toFriendlyDateTimeString(record.created_at, {
              language: getCurrentLocale(),
            }),
            action_name: getTranslatedString(`landlordPortal.incentives.${record.action}Action`),
          };
        });
        setDataSource(ds);
      })
      .catch((error) => {
        showFailure(error);
        setLoading(false);
      });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const handleResetButtonClick = () => {
    setSortedInfo(null);
    setFilteredInfo(null);
  };

  const columns = [
    {
      dataIndex: 'created_at',
      dataType: 'text',
      key: 'id',
      ellipsis: true,
      title: getTranslatedString('landlordPortal.incentives.dateAndTime'),
      width: '20%',
      sortOrder: sortedInfo?.columnKey === 'created_at' && sortedInfo?.order,
      sorter: (a, b) => basicSort(a.created_at, b.created_at),
      searchable: true,
    },
    {
      dataIndex: 'variant',
      dataType: 'text',
      key: 'variant',
      ellipsis: true,
      title: getTranslatedString('landlordPortal.incentives.variant'),
      searchable: true,
      width: '40%',
    },
    {
      dataIndex: 'action_name',
      dataType: 'text',
      key: 'action_name',
      ellipsis: true,
      title: getTranslatedString('landlordPortal.incentives.action'),
      searchable: true,
    },
    {
      dataIndex: 'user_email',
      dataType: 'text',
      key: 'user_email',
      ellipsis: true,
      title: getTranslatedString('landlordPortal.incentives.user'),
      filteredValue: filteredInfo?.user_email || null,
      searchable: true,
    },
    {
      dataIndex: 'remote_address',
      dataType: 'text',
      key: 'remote_address',
      ellipsis: true,
      title: getTranslatedString('landlordPortal.incentives.ipAddress'),
      sortOrder: sortedInfo?.columnKey === 'remote_address' && sortedInfo?.order,
      sorter: (a, b) => basicSort(a.remote_address, b.remote_address),
      filteredValue: filteredInfo?.remote_address || null,
      searchable: true,
    },
  ];

  return (
    <>
      <Row gutter={16}>
        <Col sm={24}>
          <StyledSectionContainer>
            <Row>
              <StyledTitle>History</StyledTitle>
            </Row>
            <Table
              loading={loading}
              dataSource={dataSource}
              columns={columns}
              title={null}
              resetButtonOnClick={handleResetButtonClick}
              onChange={handleTableChange}
              expandedRowRender={(record) => <ExpandedRow record={record} />}
              rowExpandable={(record) => record.action === 'update'}
            />
          </StyledSectionContainer>
        </Col>
      </Row>
    </>
  );
};

export default connect(null, { showSuccess, showFailure })(History);
